import { NotificationKind } from './enums';

/** The tabs on /notifications, and the only grouping of kinds into them. */
export const NotificationCategory = {
  PROTECTION: 'protection',
  SCANS: 'scans',
  REMOVALS: 'removals',
  ACCOUNT: 'account',
} as const;

export type NotificationCategory = (typeof NotificationCategory)[keyof typeof NotificationCategory];

export const NOTIFICATION_CATEGORY: Readonly<Record<NotificationKind, NotificationCategory>> = {
  finding: 'protection',
  weekly_digest: 'scans',
  scan_completed: 'scans',
  scan_failed: 'scans',
  case_opened: 'removals',
  case_update: 'removals',
  case_resolved: 'removals',
  authorization: 'account',
  quota: 'account',
  setup: 'account',
  support_reply: 'account',
  support_update: 'account',
};

/**
 * How loudly a row is drawn. `action` only while the problem the message is
 * about is still outstanding: the writer names it in `meta.actionKey`, and the
 * caller passes the keys `OutstandingAction` currently holds. Reading the
 * message never clears it; fixing the problem does.
 */
export type NotificationPriority = 'action' | 'important' | 'routine';

const ROUTINE: ReadonlySet<string> = new Set<NotificationKind>([
  NotificationKind.WEEKLY_DIGEST,
  NotificationKind.SCAN_COMPLETED,
  NotificationKind.CASE_OPENED,
  NotificationKind.SETUP,
]);

export function notificationPriority(
  kind: string,
  meta: Record<string, unknown> | null,
  outstanding: ReadonlySet<string> = new Set(),
): NotificationPriority {
  const key = meta?.actionKey;
  if (typeof key === 'string' && outstanding.has(key)) return 'action';
  return ROUTINE.has(kind) ? 'routine' : 'important';
}

export function notificationCategory(kind: string): NotificationCategory {
  return NOTIFICATION_CATEGORY[kind as NotificationKind] ?? 'account';
}

/**
 * Where a message leads. By kind rather than one row: after "four new
 * detections" she wants the list, and a link to one of the four would make the
 * other three feel handled. Authorization is the exception, because there the
 * profile is the thing to fix.
 */
export function notificationDestination(
  kind: string,
  meta: Record<string, unknown> | null,
): { href: string; label: string } | null {
  switch (kind) {
    case 'finding':
      return { href: '/leaks', label: 'View detections' };
    case 'scan_completed':
    case 'scan_failed':
      return { href: '/reports', label: 'Open scan history' };
    case 'weekly_digest':
      return { href: '/dashboard', label: 'Open the overview' };
    case 'case_opened':
    case 'case_update':
    case 'case_resolved':
      return { href: '/cases', label: 'View removals' };
    case 'authorization': {
      const profileId = typeof meta?.profileId === 'string' ? meta.profileId : null;
      return profileId
        ? { href: `/profiles/${profileId}/edit?tab=authorization`, label: 'Authorize profile' }
        : { href: '/profiles', label: 'Open your profiles' };
    }
    case 'support_reply':
    case 'support_update': {
      const ticketId = typeof meta?.ticketId === 'string' ? meta.ticketId : null;
      return ticketId
        ? { href: `/help/tickets/${ticketId}`, label: 'Open ticket' }
        : { href: '/help', label: 'Open Help & Support' };
    }
    case 'quota':
      return { href: '/plan', label: 'See your plan' };
    case 'setup':
      return meta?.missing === 'whitelist'
        ? { href: '/whitelist', label: 'List your pages' }
        : { href: '/profiles', label: 'Add photos' };
    default:
      return null;
  }
}
